import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import {
  Form, Input, InputNumber, Select, DatePicker, Button, Space, Spin, Alert, message,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  getOpportunity,
  createOpportunity,
  updateOpportunity,
  type CreateOpportunityRequest,
  type UpdateOpportunityRequest,
} from '@/api/opportunityApi';
import {
  listPipelines, listStages,
  type Pipeline, type PipelineStage,
} from '@/api/pipelineApi';
import { listCustomers, type Customer } from '@/api/customerApi';
import { listContacts, type Contact } from '@/api/contactApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

const STATUS_OPTIONS = ['open', 'won', 'lost', 'suspended'];

interface OpportunityFormValues {
  name: string;
  description?: string;
  customer_id: string;
  contact_id?: string;
  pipeline_id: string;
  stage_id: string;
  amount?: number;
  currency?: string;
  close_date?: dayjs.Dayjs | null;
  probability?: number;
  owner_id?: string;
  source?: string;
  status?: string;
  notes?: string;
  tags?: string[];
}

interface SelectOption {
  value: string;
  label: string;
}

export default function OpportunityForm() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<OpportunityFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lookup options
  const [customerOptions, setCustomerOptions] = useState<SelectOption[]>([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [contactOptions, setContactOptions] = useState<SelectOption[]>([]);
  const [contactSearching, setContactSearching] = useState(false);
  const [pipelineOptions, setPipelineOptions] = useState<SelectOption[]>([]);
  const [stageOptions, setStageOptions] = useState<SelectOption[]>([]);
  const [stagesLoading, setStagesLoading] = useState(false);

  const prefilledPipelineId = searchParams.get('pipeline_id') ?? '';
  const customerSearchTimer = useRef<ReturnType<typeof setTimeout>>();
  const contactSearchTimer = useRef<ReturnType<typeof setTimeout>>();

  // --- Customer search ---
  const searchCustomers = useCallback((keyword: string) => {
    if (customerSearchTimer.current) clearTimeout(customerSearchTimer.current);
    customerSearchTimer.current = setTimeout(async () => {
      setCustomerSearching(true);
      try {
        const res = await listCustomers({ from: 0, size: 20, keyword: keyword || undefined });
        setCustomerOptions(
          res.items.map((c: Customer) => ({
            value: c.id,
            label: `${c.name}${c.company ? ` (${c.company})` : ''}`,
          }))
        );
      } catch {
        setCustomerOptions([]);
      } finally {
        setCustomerSearching(false);
      }
    }, 300);
  }, []);

  // --- Contact search ---
  const searchContacts = useCallback((keyword: string) => {
    if (contactSearchTimer.current) clearTimeout(contactSearchTimer.current);
    contactSearchTimer.current = setTimeout(async () => {
      setContactSearching(true);
      try {
        const res = await listContacts({ from: 0, size: 20, keyword: keyword || undefined });
        setContactOptions(
          res.items.map((c: Contact) => ({
            value: c.id,
            label: `${c.name}${c.email ? ` (${c.email})` : ''}`,
          }))
        );
      } catch {
        setContactOptions([]);
      } finally {
        setContactSearching(false);
      }
    }, 300);
  }, []);

  // --- Load pipelines ---
  useEffect(() => {
    listPipelines({ from: 0, size: 100 })
      .then((res) =>
        setPipelineOptions(res.items.map((p: Pipeline) => ({ value: p.id, label: p.name })))
      )
      .catch(() => setPipelineOptions([]));
  }, []);

  // --- Load stages when pipeline changes ---
  const loadStagesForPipeline = useCallback(async (pipelineId: string) => {
    if (!pipelineId) {
      setStageOptions([]);
      return;
    }
    setStagesLoading(true);
    try {
      const res = await listStages(pipelineId);
      setStageOptions(
        res.items.map((s: PipelineStage) => ({ value: s.id, label: s.name }))
      );
    } catch {
      setStageOptions([]);
    } finally {
      setStagesLoading(false);
    }
  }, []);

  const handlePipelineChange = useCallback(
    (pipelineId: string) => {
      form.setFieldValue('stage_id', undefined);
      loadStagesForPipeline(pipelineId);
    },
    [form, loadStagesForPipeline]
  );

  // --- Initial data ---
  useEffect(() => {
    searchCustomers('');
    searchContacts('');
    return () => {
      if (customerSearchTimer.current) clearTimeout(customerSearchTimer.current);
      if (contactSearchTimer.current) clearTimeout(contactSearchTimer.current);
    };
  }, [searchCustomers, searchContacts]);

  // Load prefilled pipeline stages
  useEffect(() => {
    if (prefilledPipelineId && !isEdit) {
      loadStagesForPipeline(prefilledPipelineId);
    }
  }, [prefilledPipelineId, isEdit, loadStagesForPipeline]);

  // --- Edit mode: load existing opportunity ---
  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getOpportunity(id)
      .then((opp) => {
        // Load stages for the opportunity's pipeline
        loadStagesForPipeline(opp.pipeline_id);

        form.setFieldsValue({
          name: opp.name,
          description: opp.description || undefined,
          customer_id: opp.customer_id,
          contact_id: opp.contact_id || undefined,
          pipeline_id: opp.pipeline_id,
          stage_id: opp.stage_id,
          amount: opp.amount || undefined,
          currency: opp.currency || 'TWD',
          close_date: opp.close_date ? dayjs(opp.close_date) : undefined,
          probability: opp.probability,
          owner_id: opp.owner_id || undefined,
          source: opp.source || undefined,
          status: opp.status || 'open',
          notes: opp.notes || undefined,
          tags: (opp.tags?.length ?? 0) > 0 ? opp.tags : undefined,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t, loadStagesForPipeline]);

  const handleSubmit = async (values: OpportunityFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      const closeDate = values.close_date ? values.close_date.toISOString() : undefined;

      if (isEdit && id) {
        const data: UpdateOpportunityRequest = {
          name: values.name,
          description: values.description || undefined,
          customer_id: values.customer_id,
          contact_id: values.contact_id || undefined,
          pipeline_id: values.pipeline_id,
          stage_id: values.stage_id,
          amount: values.amount ?? 0,
          currency: values.currency || 'TWD',
          close_date: closeDate,
          probability: values.probability ?? 0,
          owner_id: values.owner_id || undefined,
          source: values.source || undefined,
          status: values.status || undefined,
          notes: values.notes || undefined,
          tags: values.tags || undefined,
        };
        await updateOpportunity(id, data);
        message.success(t('opportunities.update_success'));
        navigate(`/opportunities/${id}`);
      } else {
        const data: CreateOpportunityRequest = {
          name: values.name,
          description: values.description || undefined,
          customer_id: values.customer_id,
          contact_id: values.contact_id || undefined,
          pipeline_id: values.pipeline_id,
          stage_id: values.stage_id,
          amount: values.amount ?? 0,
          currency: values.currency || 'TWD',
          close_date: closeDate,
          probability: values.probability ?? 0,
          owner_id: values.owner_id || undefined,
          source: values.source || undefined,
          notes: values.notes || undefined,
          tags: values.tags || undefined,
        };
        const created = await createOpportunity(data);
        message.success(t('opportunities.create_success'));
        navigate(`/opportunities/${created.id}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('app.error');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission('opportunities', 'write')) {
    return <Navigate to="/opportunities" replace />;
  }

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(isEdit ? `/opportunities/${id}` : '/opportunities')}
        >
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('opportunities.edit_title') : t('opportunities.create_title')}</h2>

      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          status: 'open',
          currency: 'TWD',
          probability: 0,
          amount: 0,
          pipeline_id: prefilledPipelineId || undefined,
        }}
        style={{ maxWidth: 800 }}
      >
        <Form.Item
          name="name"
          label={t('opportunities.field.name')}
          rules={[
            { required: true, message: t('opportunities.validation.name_required') },
            { max: 200, message: t('opportunities.validation.name_max_length') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="customer_id"
          label={t('opportunities.field.customer_id')}
          rules={[{ required: true, message: t('opportunities.validation.customer_id_required') }]}
        >
          <Select
            showSearch
            filterOption={false}
            onSearch={searchCustomers}
            loading={customerSearching}
            options={customerOptions}
            placeholder={t('opportunities.select_customer')}
          />
        </Form.Item>

        <Form.Item name="contact_id" label={t('opportunities.field.contact_id')}>
          <Select
            showSearch
            filterOption={false}
            onSearch={searchContacts}
            loading={contactSearching}
            options={contactOptions}
            placeholder={t('opportunities.select_contact')}
            allowClear
          />
        </Form.Item>

        <Form.Item
          name="pipeline_id"
          label={t('opportunities.field.pipeline_id')}
          rules={[{ required: true, message: t('opportunities.validation.pipeline_id_required') }]}
        >
          <Select
            options={pipelineOptions}
            placeholder={t('opportunities.select_pipeline')}
            onChange={handlePipelineChange}
          />
        </Form.Item>

        <Form.Item
          name="stage_id"
          label={t('opportunities.field.stage_id')}
          rules={[{ required: true, message: t('opportunities.validation.stage_id_required') }]}
        >
          <Select
            options={stageOptions}
            placeholder={t('opportunities.select_stage')}
            loading={stagesLoading}
            disabled={stageOptions.length === 0}
          />
        </Form.Item>

        <Space size="large" style={{ display: 'flex' }}>
          <Form.Item
            name="amount"
            label={t('opportunities.field.amount')}
            rules={[{ type: 'number', min: 0, message: t('opportunities.validation.amount_min') }]}
          >
            <InputNumber min={0} style={{ width: 200 }} />
          </Form.Item>

          <Form.Item
            name="currency"
            label={t('opportunities.field.currency')}
            rules={[{ max: 3, message: t('opportunities.validation.currency_max_length') }]}
          >
            <Input style={{ width: 100 }} />
          </Form.Item>
        </Space>

        <Space size="large" style={{ display: 'flex' }}>
          <Form.Item
            name="probability"
            label={t('opportunities.field.probability')}
            rules={[
              { type: 'number', min: 0, max: 100, message: t('opportunities.validation.probability_range') },
            ]}
          >
            <InputNumber min={0} max={100} style={{ width: 150 }} addonAfter="%" />
          </Form.Item>

          <Form.Item name="close_date" label={t('opportunities.field.close_date')}>
            <DatePicker style={{ width: 200 }} />
          </Form.Item>
        </Space>

        <Form.Item name="owner_id" label={t('opportunities.field.owner_id')}>
          <Input />
        </Form.Item>

        <Form.Item
          name="source"
          label={t('opportunities.field.source')}
          rules={[{ max: 100, message: t('opportunities.validation.source_max_length') }]}
        >
          <Input />
        </Form.Item>

        {isEdit && (
          <Form.Item name="status" label={t('opportunities.field.status')}>
            <Select>
              {STATUS_OPTIONS.map((s) => (
                <Select.Option key={s} value={s}>
                  {t(`opportunities.status_options.${s}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="description"
          label={t('opportunities.field.description')}
          rules={[{ max: 2000, message: t('opportunities.validation.description_max_length') }]}
        >
          <TextArea rows={3} />
        </Form.Item>

        <Form.Item
          name="notes"
          label={t('opportunities.field.notes')}
          rules={[{ max: 2000, message: t('opportunities.validation.notes_max_length') }]}
        >
          <TextArea rows={3} />
        </Form.Item>

        <Form.Item name="tags" label={t('opportunities.field.tags')}>
          <Select mode="tags" tokenSeparators={[',']} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate(isEdit ? `/opportunities/${id}` : '/opportunities')}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
